import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** Whether showDialog() opened the dialog and closeDialog() has not run yet. */
  #isOpen = false;

  connectedCallback() {
    super.connectedCallback();

    // EFS: Android's back gesture (and other platform close requests) close a modal
    // <dialog> natively via "cancel"/"close", bypassing closeDialog(). Route them
    // through closeDialog(), and clean up if the browser closes it anyway.
    // Neither event bubbles, so listen in the capture phase on the host.
    this.addEventListener('cancel', this.#handleCancel, true);
    this.addEventListener('close', this.#handleNativeClose, true);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('cancel', this.#handleCancel, true);
    this.removeEventListener('close', this.#handleNativeClose, true);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Scroll-Lock nur setzen wenn body nicht bereits von einer anderen Komponente gelockt ist
    // (z.B. Bottom Sheet). Sonst wird body.style.top überschrieben und die Seite springt.
    this._ownsScrollLock = document.body.style.position !== 'fixed';
    if (this._ownsScrollLock) {
      document.body.style.width = '100%';
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    }

    dialog.showModal();
    this.#isOpen = true;
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.#isOpen = false;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.#releaseScrollLock();

    dialog.close();
    // Warte auf den nächsten Frame, damit der Browser das durch `dialog.close()`
    // entstandene `display: none` (bzw. das Entfernen des [open]-Attributs) bereits
    // gepaintet hat, bevor wir die `dialog-closing`-Klasse entfernen. Sonst fällt
    // der `animation: ... forwards`-Endzustand (opacity: 0) weg, während der Dialog
    // noch rendert, und der Inhalt blitzt kurz auf.
    requestAnimationFrame(() => {
      dialog.classList.remove('dialog-closing');
    });

    this.dispatchEvent(new DialogCloseEvent());
  };

  /** Scroll-Lock nur aufheben wenn dieser Dialog ihn gesetzt hat. */
  #releaseScrollLock() {
    if (!this._ownsScrollLock) return;
    this._ownsScrollLock = false;

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });
  }

  /**
   * Platform close request (Android back gesture, Esc without our keydown handler):
   * animate out like any other close.
   * @param {Event} event
   */
  #handleCancel = (event) => {
    if (event.target !== this.refs.dialog) return;
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * The browser may close the dialog anyway (e.g. repeated back gestures cannot be
   * cancelled). Without this, body would stay position: fixed and the page frozen.
   * @param {Event} event
   */
  #handleNativeClose = (event) => {
    if (event.target !== this.refs.dialog || !this.#isOpen) return;

    this.#isOpen = false;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog.classList.remove('dialog-closing');
    this.#releaseScrollLock();
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
